/**
 * @file Frontend API client — adminEventService.
 */

import axiosClient from "../api/axiosClient";

/** Type definition — Admin Event. */
export interface AdminEvent {
  id: number;
  title: string;
  content: string;
  eventDate: string;
  place: string;
  image: string | null;
  registrationLink: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Type definition — Admin Event Create Body. */
export interface AdminEventCreateBody {
  title: string;
  content: string;
  eventDate: string; // ISO
  place: string;
  image?: string;
  registrationLink?: string;
}

/** Type definition — Admin Event Update Body. */
export type AdminEventUpdateBody = Partial<AdminEventCreateBody> & {
  isActive?: boolean;
};

/** Type definition — Api Response. */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/** Type definition — List Response. */
export interface ListResponse<T> {
  success: boolean;
  data: T[];
}

/** Exported const — admin Event Service. */
export const adminEventService = {
  async list(includeInactive: boolean = false) {
    return axiosClient.get<ListResponse<AdminEvent>>("/admin/events", {
      params: includeInactive ? { includeInactive: true } : undefined,
    });
  },

  async create(body: AdminEventCreateBody) {
    return axiosClient.post<ApiResponse<AdminEvent>>("/admin/events", body);
  },

  async createMultipart(formData: FormData) {
    return axiosClient.post<ApiResponse<AdminEvent>>("/admin/events", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  async getById(id: number) {
    return axiosClient.get<ApiResponse<AdminEvent>>(`/admin/events/${id}`);
  },

  async update(id: number, body: AdminEventUpdateBody) {
    return axiosClient.put<ApiResponse<AdminEvent>>(`/admin/events/${id}`, body);
  },

  async updateMultipart(id: number, formData: FormData) {
    return axiosClient.put<ApiResponse<AdminEvent>>(`/admin/events/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  async remove(id: number) {
    return axiosClient.delete<ApiResponse<{ id: number }>>(`/admin/events/${id}`);
  },
};

